import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import React, { useCallback, useRef } from 'react';
import c from 'classnames';
import isFunction from 'lodash/isFunction';
import AutoCompleteLayout from '../../layouts/AutoCompleteLayout';
import BaseInput from '../BaseInput';
import InputFieldOutline from '../InputFieldOutline';
import RemovableTag from '../../../TagList/RemovableTag';
import s from './TagInputField.scss';
function hasTagAlready(existingTags, newTag) {
    for(let i = 0; i < existingTags.length; i++){
        const existingTag = existingTags[i];
        if (existingTag.toLowerCase() === newTag.toLowerCase()) {
            return true;
        }
    }
    return false;
}
const TagInputField = /*#__PURE__*/ React.forwardRef((_param, ref)=>{
    var { id, tags = [], tagSuggestions, fieldClassname, onAddTag, onRemoveTag, onKeyDown, onCancel, value, validationResult, placeholder, // TODO: Handle `isRequired`.
    // Not sure how to structure this as of writting since I don't know if this will ever
    // be applicable
    isRequired, disabled, valueAsFirstSuggestion } = _param, rest = _object_without_properties(_param, [
        "id",
        "tags",
        "tagSuggestions",
        "fieldClassname",
        "onAddTag",
        "onRemoveTag",
        "onKeyDown",
        "onCancel",
        "value",
        "validationResult",
        "placeholder",
        "isRequired",
        "disabled",
        "valueAsFirstSuggestion"
    ]);
    const localInputRef = useRef(null);
    // Wrapped to prevent duplicate tags being submitted
    const handleAddTag = useCallback((tag)=>{
        if (!hasTagAlready(tags, tag)) {
            onAddTag(tag);
        }
    }, [
        onAddTag,
        tags
    ]);
    const handleKeyDown = useCallback((e)=>{
        switch(e.key){
            case ',':
            case ' ':
            case 'Space':
                // Prevent empty tags being added
                if (value.length > 0) {
                    handleAddTag(value);
                }
                e.preventDefault();
                e.stopPropagation();
                break;
            case 'Backspace':
                if (value === '' && tags.length > 0) {
                    onRemoveTag(tags[tags.length - 1]);
                    e.preventDefault();
                    e.stopPropagation();
                }
                break;
            case 'Escape':
                onCancel();
                e.preventDefault();
                e.stopPropagation();
                break;
            default:
                break;
        }
        onKeyDown && onKeyDown(e);
    }, [
        value,
        onRemoveTag,
        onKeyDown,
        onCancel,
        handleAddTag,
        tags
    ]);
    const handleAutoCompleteSelect = useCallback((autoCompleteItem)=>{
        var _localInputRef_current;
        handleAddTag(autoCompleteItem.value);
        (_localInputRef_current = localInputRef.current) === null || _localInputRef_current === void 0 ? void 0 : _localInputRef_current.focus();
    }, [
        handleAddTag
    ]);
    // Add input as first suggestion
    const freeformSuggestion = [];
    if (valueAsFirstSuggestion && value && value.trim() !== '') {
        freeformSuggestion.push({
            label: value,
            value
        });
    }
    return /*#__PURE__*/ React.createElement(AutoCompleteLayout, {
        items: [
            ...freeformSuggestion,
            ...tagSuggestions.map((suggestion)=>({
                    label: suggestion,
                    value: suggestion
                }))
        ],
        onSelect: handleAutoCompleteSelect,
        onKeyDown: handleKeyDown,
        menuId: `autocomplete-${id}`,
        className: s['autocomplete-layout']
    }, /*#__PURE__*/ React.createElement(InputFieldOutline, {
        className: c(s['outline-root'], fieldClassname),
        outlineStyle: disabled ? 'disabled' : validationResult,
        onClick: ()=>localInputRef.current && localInputRef.current.focus()
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['content-wrapper']
    }, tags.map((tag, index)=>/*#__PURE__*/ React.createElement(RemovableTag, {
            size: "small",
            key: `tag-${index}`,
            className: s['tag'],
            onClick: ()=>onRemoveTag(tag)
        }, tag)), /*#__PURE__*/ React.createElement(BaseInput, _object_spread({
        id: id,
        disabled: disabled,
        value: value,
        className: s['input'],
        placeholder: tags.length === 0 ? placeholder : undefined,
        // We're providing our own autocomplete
        autoComplete: "off",
        ref: (node)=>{
            if (isFunction(ref)) {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
            localInputRef.current = node;
        }
    }, rest)))));
});
TagInputField.displayName = 'TagInputField';
export default TagInputField;
